import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ViewProductModelsVariants from './ViewProductModelsVariants'
import AddEditProductModelsVariants from './AddEditProductModelsVariants'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, DownloadIcon, EnabledIcon, DisabledIcon } from '../../../icons'
import { productModelsDeletePermissions, productModelsWritePermissions } from '../../../utils/permissionValidation'
import { capitalizeFirstLetter, downloadFile } from '../../../utils/functions'
import { clearProductModelsMessages, downloadModelVariantsCSV, loadProductModelsVariants, removeProductModelsVariants } from '../../../actions/productModels'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'modelNumber',
    order: 'asc',
    translationKey: 'model_number_asc',
  },
  {
    field: 'modelNumber',
    order: 'desc',
    translationKey: 'model_number_desc',
  },
  {
    field: 'model',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'model',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'productType',
    order: 'asc',
    translationKey: 'product_type_asc',
  },
  {
    field: 'productType',
    order: 'desc',
    translationKey: 'product_type_desc',
  },
  {
    field: 'category',
    order: 'asc',
    translationKey: 'category_asc',
  },
  {
    field: 'category',
    order: 'desc',
    translationKey: 'category_desc',
  },
  {
    field: 'serialNumberPrefix',
    order: 'asc',
    translationKey: 'serial_number_prefix_asc',
  },
  {
    field: 'serialNumberPrefix',
    order: 'desc',
    translationKey: 'serial_number_prefix_desc',
  },
  {
    field: 'productModelName',
    order: 'asc',
    translationKey: 'product_model_name_asc',
  },
  {
    field: 'productModelName',
    order: 'desc',
    translationKey: 'product_model_name_desc',
  },
  {
    field: 'status',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'status',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Variants = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [productModelsVariantsTableData, setProductModelsVariantsTableData] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [selectedFilterProductType, setSelectedFilterProductType] = useState(null)
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null)
  const [selectedBrandFilter, setSelectedBrandFilter] = useState([])
  const [statusesFilter, setStatusesFilter] = useState([])
  const [modelCategoryFilter, setModelCategoryFilter] = useState([])
  const [selectedModelDetails, setSelectedModelDetails] = useState(null)
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedVariant, setExpandedVariant] = useState(0)
  const [showAddEditProductModelsVariantsModal, setShowAddEditProductModelsVariantsModal] = useState(false)
  const [showViewProductModelsVariantsModal, setShowViewProductModelsVariantsModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [variantIdToDelete, setVariantIdToDelete] = useState(null)
  const statuses = props.variantStatuses
  const modelCategories = props.modelCategory
  const hasTable = productModelsVariantsTableData?.length > 0
  const brandLabels = props.brands?.map((b) => {
    return b.brand || []
  })
  const categoryLabels = props.productCategories?.map((pc) => {
    return pc.category || []
  })
  const productTypeLabels = props.productTypes?.map((pt) => {
    return pt.productType || []
  })
  const modelLabels = props.models?.map((m) => {
    return m.model || []
  })

  const toTitle = (s = '') => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

  const modelCategoryValueMap = (modelCategories || []).reduce((acc, v) => {
    acc[toTitle(v)] = v
    return acc
  }, {})

  const modelCategoryLabelMap = (modelCategories || []).reduce((acc, v) => {
    acc[v] = toTitle(v)
    return acc
  }, {})

  useEffect(() => {
    const tableData = props.productModelsVariantsTableData?.map((item, index) => {
      const deviceModel = item.deviceModel || {}
      return {
        index,
        _original: item,
        modelNumberId: item.id,
        modelId: deviceModel.id || '',
        modelNumber: item.modelNumber || '',
        model: deviceModel.name?.toUpperCase() || '',
        productType: deviceModel.productType?.toUpperCase() || '',
        category: deviceModel.category || '',
        serialNumberPrefix: item.serialNumberPrefix || '',
        brand: deviceModel.brand || '',
        status: item.status || '',
        modelCategory: item.modelCategory || '',
        productModelName: item.productModelName || '',
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item, true)
            },
            requiredPermission: productModelsWritePermissions,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(item)
            },
            requiredPermission: productModelsDeletePermissions,
            isDisabled: item.status !== 'inactive',
          },
        ],
      }
    })

    setProductModelsVariantsTableData(tableData)
  }, [props.productModelsVariantsTableData])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.errorMessage, props.actions])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.successMessage, props.actions])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    const changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadTableData(true)
        }
        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterModel, selectedFilterProductType, selectedFilterCategory, statusesFilter, modelCategoryFilter, selectedBrandFilter, orderBy])

  const handleDownload = () => {
    let queryParams = createQueryParams()
    queryParams = {
      ...queryParams,
    }

    props.actions.downloadModelVariantsCSV(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')
      downloadFile({
        data: res.value,
        fileName: `product_models_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams()
    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }
    props.actions.loadProductModelsVariants(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 1) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel(null)
    setSelectedFilterProductType(null)
    setSelectedFilterCategory(null)
    setStatusesFilter([])
    setModelCategoryFilter([])
    setSelectedBrandFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterModel) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterProductType) {
      queryParams = { ...queryParams, productType: selectedFilterProductType }
    }

    if (selectedFilterCategory) {
      queryParams = { ...queryParams, category: selectedFilterCategory }
    }

    if (statusesFilter.length > 0) {
      queryParams = { ...queryParams, status: statusesFilter }
    }

    if (modelCategoryFilter.length > 0) {
      queryParams = { ...queryParams, modelCategory: modelCategoryFilter }
    }

    if (selectedBrandFilter.length > 0) {
      queryParams = { ...queryParams, brand: selectedBrandFilter }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEdit = (modelDetails, isEditModal) => {
    setSelectedModelDetails(modelDetails)
    isEditModal ? setShowAddEditProductModelsVariantsModal(true) : setShowViewProductModelsVariantsModal(true)
  }

  const showConfirmationModal = (item) => {
    setVariantIdToDelete(item)
    setShowDeleteConfirmModal(true)
  }

  const handleDeleteVariant = () => {
    setShowDeleteConfirmModal(false)
    props.actions.removeProductModelsVariants(variantIdToDelete?.deviceModel?.id, variantIdToDelete?.id)
  }

  const closeModal = () => {
    props.actions.clearProductModelsMessages()
    setSelectedModelDetails(null)
    setShowAddEditProductModelsVariantsModal(false)
    setShowViewProductModelsVariantsModal(false)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderStatus = (status) => {
    switch (status) {
      case statuses[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case statuses[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      case statuses[2]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('obsolete')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper flex-justify-between">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={
              selectedFilterModel ||
              selectedFilterProductType ||
              selectedFilterCategory ||
              statusesFilter.length > 0 ||
              modelCategoryFilter.length > 0 ||
              selectedBrandFilter.length > 0
            }
            counterBadge={
              (selectedFilterModel && 1) +
              (selectedFilterProductType && 1) +
              (selectedFilterCategory && 1) +
              modelCategoryFilter.length +
              statusesFilter.length +
              selectedBrandFilter.length
            }
            minSearchLength={2}
          />
          <div className="unit-languages-download">
            <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isLoading || !hasTable}>
              <DownloadIcon />
              {t('download')}
            </SimpleButton>
            <SimpleButton
              className="submit-button"
              onClick={() => setShowAddEditProductModelsVariantsModal(true)}
              requiredPermission={productModelsWritePermissions}
            >
              {t('product_models_context.add_new_variant')}
            </SimpleButton>
          </div>
        </div>

        {isMobile && productModelsVariantsTableData?.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}

        {!isMobile && productModelsVariantsTableData?.length > 0 && (
          <GenericTable
            data={productModelsVariantsTableData}
            headers={[
              {
                title: t('device_context.model_number'),
                orderKey: 'modelNumber',
              },
              {
                title: t('model'),
                orderKey: 'model',
              },
              {
                title: t('product_type'),
                orderKey: 'productType',
              },
              {
                title: t('category'),
                orderKey: 'category',
              },
              {
                title: t('product_models_context.serial_number_prefix'),
                orderKey: 'serialNumberPrefix',
              },
              {
                title: t('product_models_context.product_model_name'),
                orderKey: 'productModelName',
              },
              {
                title: t('status'),
                orderKey: 'status',
              },
              {
                title: '',
              },
            ]}
            keys={['modelNumber', 'model', 'productType', 'category', 'serialNumberPrefix', 'productModelName', 'status', 'action']}
            keyRenderer={{
              status: (item) => {
                return <div className="status-wrapper">{renderStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(item) => handleOpenViewEdit(item._original)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}

        {isMobile &&
          productModelsVariantsTableData?.map((item, index) => (
            <div key={index} className="card" onClick={() => setExpandedVariant(index)}>
              <div className={'card-item' + (expandedVariant !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('device_context.model_number')}</div>
                <div className={'card-item-body' + (expandedVariant !== index ? ' align-center' : '')}>
                  <div>{item.modelNumber}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedVariant === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('model')}</div>
                    <div className="card-item-body">{item.model}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('product_type')}</div>
                    <div className="card-item-body">{item.productType}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('category')}</div>
                    <div className="card-item-body">{item.category}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('product_models_context.serial_number_prefix')}</div>
                    <div className="card-item-body">{item.serialNumberPrefix}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('product_models_context.product_model_name')}</div>
                    <div className="card-item-body">{item.productModelName}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderStatus(item.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}

        {!showSpinner && !isFirstLoad && productModelsVariantsTableData?.length === 0 && (
          <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
        )}

        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>

      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: modelLabels,
              input: selectedFilterModel,
              output: (model) => {
                setSelectedFilterModel(model)
              },
            },
            {
              name: t('product_type'),
              type: FilterType.singleSelect,
              dataset: productTypeLabels,
              input: selectedFilterProductType,
              output: (type) => {
                setSelectedFilterProductType(type)
              },
            },
            {
              name: t('category'),
              type: FilterType.singleSelect,
              dataset: categoryLabels,
              input: selectedFilterCategory,
              output: (category) => {
                setSelectedFilterCategory(category)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: statuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
            {
              name: t('product_models_context.model_category'),
              type: FilterType.multiSelect,
              dataset: (modelCategories || []).map(toTitle),
              input: (modelCategoryFilter || []).map((v) => modelCategoryLabelMap[v] || toTitle(v)),
              output: (selectedLabels) => {
                const apiValues = (selectedLabels || []).map((label) => modelCategoryValueMap[label] || label)
                setModelCategoryFilter(apiValues)
              },
            },
            {
              name: t('dealer-portal.brand'),
              type: FilterType.multiSelect,
              dataset: brandLabels,
              input: selectedBrandFilter,
              output: (brand) => {
                setSelectedBrandFilter(brand)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}

      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}

      {showAddEditProductModelsVariantsModal && (
        <AddEditProductModelsVariants modelDetails={selectedModelDetails} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}

      {showViewProductModelsVariantsModal && selectedModelDetails && (
        <ViewProductModelsVariants
          modelDetails={selectedModelDetails}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditModel={() => {
            setShowViewProductModelsVariantsModal(false)
            setShowAddEditProductModelsVariantsModal(true)
          }}
          closeModal={closeModal}
        />
      )}

      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteConfirmModal(false)}
          onAccept={() => handleDeleteVariant()}
          onCancel={() => setShowDeleteConfirmModal(false)}
          message={t('product_models_context.delete_model_confirmation')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={productModelsDeletePermissions}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ documents, productModels, shared }) {
  return {
    productModelsVariantsTableData: productModels?.productModelsVariants || [],
    models: productModels?.productModelsFilterOptions?.models || [],
    modelCategory: productModels?.productModelsFilterOptions?.modelCategory || [],
    productTypes: productModels?.productModelsFilterOptions?.productTypes || [],
    productCategories: productModels?.productModelsFilterOptions?.productCategories || [],
    brands: productModels?.productModelsFilterOptions?.brands || [],
    variantStatuses: productModels?.productModelsFilterOptions?.status || [],
    isLoading: productModels?.isLoading,
    canLoadMore: productModels?.canLoadMore,
    successMessage: productModels?.successMessage,
    errorMessage: productModels?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadProductModelsVariants,
        removeProductModelsVariants,
        downloadModelVariantsCSV,
        clearProductModelsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Variants)
